/**
 * Fluxo de Caixa Contratado por Book (Task #64) — CONSUMIDOR puro do
 * breakdown da Fatia C (#60c): lê `books` do /api/curva-pnl (pnl_historico,
 * servido do store — a MESMA fonte/classificação bu+override que a Mesa usa;
 * zero cálculo novo, zero classificador paralelo, pra nunca divergir do
 * "Consolidado Atacado + Varejo" da aba Fluxo de Caixa).
 *
 * Conteúdo: barras empilhadas do CF por ano (Atacado morre em 2029; a cauda
 * longa é Varejo), cortes de horizonte (até 2028 / até 2029 / total), chips
 * de Σ|CF| + duration por book, e as notas de honestidade (flag do bucket
 * "-" dentro do Atacado; FV Curta/posição aberta fora — é MtM, não cashflow
 * contratado).
 *
 * Total ≠ Atacado+Varejo por design: cada book NETEIA DENTRO de si; o Total
 * ainda neta compra×venda CRUZADA entre books no mesmo mês (delta ~R$2,6MM)
 * — rodapé explica, nada é "ajustado" pra fechar soma.
 */
import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useCurvaPnl } from "../../hooks/useCurvaPnl";
import { useCfBookDetalhe } from "../../hooks/useCfBookDetalhe";
import { fmtBRL, fmtNum } from "../../utils/formatters";
import { ExportDropdown, type ExportColumn } from "../ui/ExportDropdown";
import { brlInt, int } from "../../utils/xlsxHelpers";
import type { BookSplit } from "../../api/types";

type Row = Record<string, unknown>;

const COR_ATACADO = "#38bdf8"; // sky-400
const COR_VAREJO = "#34d399"; // emerald-400

function fmtMM(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return `${fmtNum(v / 1_000_000)}MM`;
}

function acumuladoAte(porAno: Record<string, number>, anoLimite: number): number {
  return Object.entries(porAno)
    .filter(([ano]) => Number(ano) <= anoLimite)
    .reduce((acc, [, v]) => acc + v, 0);
}

export function CfPorBookCard() {
  const { data, isLoading, error } = useCurvaPnl();
  const detalhe = useCfBookDetalhe();   // #244: detalhe por cliente pro export
  // #64 IPCA ON: toggle Nominal·Indexado. Default Nominal (comportamento atual).
  // Troca os valores da tabela inteira sem refetch — o payload traz os dois.
  const [indexado, setIndexado] = useState(false);
  const books = data.books;
  const res = data.resultado;

  // Seletores cientes do modo: indexado usa os campos *_indexado do payload
  // (fallback ao nominal em payload cacheado pré-#64, onde ainda não existem).
  const cfAno = (b: BookSplit): Record<string, number> =>
    (indexado ? b.abs_cf_por_ano_indexado : undefined) ?? b.abs_cf_por_ano;
  const sigmaB = (b: BookSplit): number | null =>
    indexado ? b.sigma_cf_indexado ?? b.sigma_cf : b.sigma_cf;
  const durB = (b: BookSplit): number | null =>
    indexado ? b.duration_indexado ?? b.duration : b.duration;

  const chartRows = useMemo(() => {
    if (!books) return [];
    const anos = new Set([
      ...Object.keys(books.atacado.abs_cf_por_ano),
      ...Object.keys(books.varejo.abs_cf_por_ano),
    ]);
    return [...anos].sort().map((ano) => ({
      ano,
      atacado: cfAno(books.atacado)[ano] ?? 0,
      varejo: cfAno(books.varejo)[ano] ?? 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [books, indexado]);

  if (isLoading) {
    return (
      <div className="text-white/40 text-sm py-12 text-center" data-testid="cf-book-loading">
        Carregando CF por book…
      </div>
    );
  }
  if (error || !books) {
    // books=null só em payload pré-#60c (cron/recompute do dia preenche).
    return (
      <div className="text-white/40 text-sm py-12 text-center" data-testid="cf-book-vazio">
        CF por book ainda não computado pro dia — aguarde o ciclo diário.
      </div>
    );
  }

  const { atacado, varejo } = books;
  // Total netado GLOBAL (cross-book) cientes do modo; nominal reconcilia com
  // resultado.sigma_cf (fallback p/ payload cacheado pré-#64 sem books.total).
  const totalSigma = indexado
    ? books.total?.sigma_cf_indexado ?? res.sigma_cf
    : books.total?.sigma_cf ?? res.sigma_cf;
  const totalDur = indexado
    ? books.total?.duration_indexado ?? res.duration
    : books.total?.duration ?? res.duration;
  const somaBooks = (sigmaB(atacado) ?? 0) + (sigmaB(varejo) ?? 0); // p/ exibição (segue o modo)
  const horizontes = [
    { label: "até 2028", ano: 2028 },
    { label: "até 2029", ano: 2029 },
    { label: "total", ano: 9999 },
  ];
  const pctSemCarteira =
    atacado.sigma_cf && atacado.sem_carteira_sigma_cf
      ? Math.round((atacado.sem_carteira_sigma_cf / atacado.sigma_cf) * 100)
      : null;

  // ---------- Export (#244) — 2 abas + as 2 divergências em coluna própria ----
  // Aba 1: consolidado book × ano (como na tela: chart + cortes de horizonte).
  // Σ sem carteira / netting cruzado ficam EXPLÍCITOS (não escondidos). O export
  // é SEMPRE NOMINAL (a aba Por Cliente é nominal-only) — independente do toggle.
  const anos = chartRows.map((r) => r.ano);
  const somaBooksNominal = (atacado.sigma_cf ?? 0) + (varejo.sigma_cf ?? 0);
  const linhaConsolidada = (nome: string, b: BookSplit): Row => {
    const row: Row = { book: nome };
    for (const a of anos) row[a] = b.abs_cf_por_ano[a] ?? 0;
    row.ate2028 = acumuladoAte(b.abs_cf_por_ano, 2028);
    row.ate2029 = acumuladoAte(b.abs_cf_por_ano, 2029);
    row.total = acumuladoAte(b.abs_cf_por_ano, 9999);
    row.sc_cf = b.sem_carteira_sigma_cf ?? 0;
    row.sc_n = b.sem_carteira_n_trades ?? 0;
    return row;
  };
  const somaPorAno = (a: string) =>
    (atacado.abs_cf_por_ano[a] ?? 0) + (varejo.abs_cf_por_ano[a] ?? 0);
  const consolidadoRows: Row[] = [
    linhaConsolidada("Atacado", atacado),
    linhaConsolidada("Varejo", varejo),
    {
      book: "Σ books",
      ...Object.fromEntries(anos.map((a) => [a, somaPorAno(a)])),
      ate2028: acumuladoAte(atacado.abs_cf_por_ano, 2028) + acumuladoAte(varejo.abs_cf_por_ano, 2028),
      ate2029: acumuladoAte(atacado.abs_cf_por_ano, 2029) + acumuladoAte(varejo.abs_cf_por_ano, 2029),
      total: acumuladoAte(atacado.abs_cf_por_ano, 9999) + acumuladoAte(varejo.abs_cf_por_ano, 9999),
      sc_cf: (atacado.sem_carteira_sigma_cf ?? 0) + (varejo.sem_carteira_sigma_cf ?? 0),
      sc_n: atacado.sem_carteira_n_trades + varejo.sem_carteira_n_trades,
    },
    { book: "Total (netado global)", total: res.sigma_cf ?? undefined },
    // Divergência (a): netting cruzado entre books = Σ books − Total.
    { book: "Netting cruzado (Σ books − Total)", total: somaBooksNominal - (res.sigma_cf ?? 0) },
  ];
  const consolidadoCols: ExportColumn<Row>[] = [
    { key: "book", header: "Book" },
    ...anos.map((a) => ({ key: a, header: a, format: (r: Row) => brlInt(r[a] as number) })),
    { key: "ate2028", header: "até 2028", format: (r: Row) => brlInt(r.ate2028 as number) },
    { key: "ate2029", header: "até 2029", format: (r: Row) => brlInt(r.ate2029 as number) },
    { key: "total", header: "Total", format: (r: Row) => brlInt(r.total as number) },
    { key: "sc_cf", header: "Σ sem carteira (R$)", format: (r: Row) => brlInt(r.sc_cf as number) },
    { key: "sc_n", header: "Sem carteira (trades)", format: (r: Row) => int(r.sc_n as number) },
  ];

  // Aba 2: POR CLIENTE (book, contraparte, cnpj, mês forn, CF líquido, n_trades),
  // com a divergência (b) flagada na própria linha (n_sem_carteira).
  const detalheRows = detalhe.data.linhas as unknown as Row[];
  const detalheCols: ExportColumn<Row>[] = [
    { key: "book", header: "Book" },
    { key: "contraparte", header: "Contraparte" },
    { key: "cnpj", header: "CNPJ", format: (r: Row) => (r.cnpj as string) ?? "" },
    {
      key: "mes_forn",
      header: "Mês forn.",
      format: (r: Row) => (r.mes_forn ? String(r.mes_forn).slice(0, 7) : ""),
    },
    { key: "cf_liquido", header: "CF líquido (R$)", format: (r: Row) => brlInt(r.cf_liquido as number) },
    { key: "n_trades", header: "Trades", format: (r: Row) => int(r.n_trades as number) },
    {
      key: "n_sem_carteira",
      header: "Sem carteira (trades)",
      format: (r: Row) => int(r.n_sem_carteira as number),
    },
  ];
  const temDetalhe = detalheRows.length > 0;

  return (
    <div
      className="rounded-xl border border-white/10 bg-indra-azul-surface p-4"
      data-testid="cf-por-book-card"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-white font-semibold">Fluxo de Caixa Contratado por Book</h3>
          <p className="text-white/50 text-xs mt-0.5">
            Σ|CF| líquido mensal por book · trades vigentes Zeus · mesma
            classificação do PnL da Mesa (#60c) · referência {res.as_of ?? "—"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* #64 IPCA ON: toggle Nominal·Indexado — troca os valores da tabela
              inteira (chips, chart, cortes) sem refetch. Default Nominal. */}
          <div
            className="inline-flex rounded-lg border border-white/10 bg-white/5 p-0.5 text-xs"
            role="group"
            aria-label="Modo de marcação do CF"
            data-testid="cf-book-toggle-indexado"
          >
            <button
              type="button"
              onClick={() => setIndexado(false)}
              aria-pressed={!indexado}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                !indexado ? "bg-white/15 text-white" : "text-white/50 hover:text-white/80"
              }`}
            >
              Nominal
            </button>
            <button
              type="button"
              onClick={() => setIndexado(true)}
              aria-pressed={indexado}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                indexado ? "bg-white/15 text-white" : "text-white/50 hover:text-white/80"
              }`}
            >
              Indexado (IPCA)
            </button>
          </div>
          {/* #244: export de conferência da Mesa — consolidado (book × ano) +
              POR CLIENTE (aba extra do .xlsx), com as 2 divergências em coluna. */}
          <ExportDropdown
            filename={`cf-por-book-${res.as_of ?? "hoje"}`}
            columns={consolidadoCols}
            rows={consolidadoRows}
            primarySheetName="Consolidado"
            extraSheets={
              temDetalhe
                ? [{ name: "Por Cliente", columns: detalheCols, rows: detalheRows }]
                : undefined
            }
            extraSheetsLabel="Tudo (Excel) — inclui Por Cliente"
          />
        </div>
      </div>

      {/* Chips: Σ|CF| + duration por book (+ Total netado global). */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4" data-testid="cf-book-chips">
        <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide" style={{ color: COR_ATACADO }}>
            Atacado
          </div>
          <div className="text-white font-mono text-sm" data-testid="chip-atacado">
            {fmtMM(sigmaB(atacado))} · {durB(atacado) !== null ? `${fmtNum(durB(atacado) as number)} anos` : "—"}
          </div>
        </div>
        <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide" style={{ color: COR_VAREJO }}>
            Varejo
          </div>
          <div className="text-white font-mono text-sm" data-testid="chip-varejo">
            {fmtMM(sigmaB(varejo))} · {durB(varejo) !== null ? `${fmtNum(durB(varejo) as number)} anos` : "—"}
          </div>
        </div>
        <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-white/60">
            Total (netado global)
          </div>
          <div className="text-white font-mono text-sm" data-testid="chip-total">
            {fmtMM(totalSigma)} · {totalDur !== null ? `${fmtNum(totalDur)} anos` : "—"}
          </div>
        </div>
      </div>

      {/* CF por ano — empilhado: escancara o Atacado morrendo em 2029 e a
          cauda longa do Varejo. */}
      <div className="h-64" data-testid="cf-book-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartRows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="ano" tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }} />
            <YAxis
              tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }}
              tickFormatter={(v: number) => `${Math.round(v / 1_000_000)}M`}
            />
            <Tooltip
              formatter={(v: number, name: string) => [fmtBRL(v, { decimals: 0 }), name]}
              contentStyle={{
                backgroundColor: "#0c2340",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 8,
              }}
              labelStyle={{ color: "rgba(255,255,255,0.8)" }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="atacado" name="Atacado" stackId="cf" fill={COR_ATACADO} />
            <Bar dataKey="varejo" name="Varejo" stackId="cf" fill={COR_VAREJO} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Cortes de horizonte — derivados do CF por ano do payload. */}
      <div className="overflow-x-auto mt-4">
        <table className="w-full" data-testid="cf-book-horizontes">
          <thead>
            <tr>
              <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">Book</th>
              {horizontes.map((h) => (
                <th key={h.label} className="text-right py-2 px-3 text-xs uppercase tracking-wide text-white/60 bg-indra-azul-deep">
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {([["Atacado", atacado], ["Varejo", varejo]] as const).map(([nome, b]) => (
              <tr key={nome} className="border-b border-white/5">
                <td className="py-2 px-3 text-sm text-white">{nome}</td>
                {horizontes.map((h) => (
                  <td key={h.label} className="py-2 px-3 text-right text-sm font-mono tabular-nums text-white" data-testid={`cf-${nome.toLowerCase()}-${h.ano}`}>
                    {fmtMM(acumuladoAte(cfAno(b), h.ano))}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t border-white/15">
              <td className="py-2 px-3 text-sm text-white/70">Σ books*</td>
              {horizontes.map((h) => (
                <td key={h.label} className="py-2 px-3 text-right text-sm font-mono tabular-nums text-white/70">
                  {fmtMM(acumuladoAte(cfAno(atacado), h.ano) + acumuladoAte(cfAno(varejo), h.ano))}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Notas de honestidade (#60c): flag do bucket "-", netting cruzado e
          o escopo (cashflow contratado, não MtM). */}
      <div className="mt-3 space-y-1 text-[11px] text-white/40 leading-relaxed" data-testid="cf-book-notas">
        {indexado && (
          <p className="text-emerald-300/70" data-testid="cf-book-nota-indexado">
            ✦ Modo Indexado: contratos reajustados pela inflação implícita (#218)
            · curva nominal — reajusta só o contrato, não a marcação.
          </p>
        )}
        <p>
          * Σ books ≠ Total (netado global, {fmtMM(totalSigma)}): cada book
          neta compra×venda dentro de si; o Total ainda neta o cruzado entre
          books no mesmo mês ({fmtMM(somaBooks - (totalSigma ?? 0))}).
        </p>
        <p data-testid="cf-book-flag-sem-carteira">
          ⚠ Dentro do Atacado, {fmtMM(atacado.sem_carteira_sigma_cf)}
          {pctSemCarteira !== null ? ` (${pctSemCarteira}% do book)` : ""} vêm de{" "}
          {atacado.sem_carteira_n_trades} trades SEM carteira no Zeus
          (comercializadoras, bucket &quot;-&quot;) — classificação em revisão pela Mesa.
        </p>
        <p data-testid="cf-book-nota-fv-curta">
          A posição aberta CCEE 2026 (MtM) NÃO entra — este card é cashflow
          CONTRATADO (trades bilaterais). {books.nota_fv_curta}
        </p>
      </div>
    </div>
  );
}
